import React, { useState, useLayoutEffect, useRef } from 'react';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';
import { css } from '@linaria/core';
import FormField from './FormField';
import FormLabel from './FormLabel';
import CloseIcon from './../icons/Close';
import ExpandMoreIcon from './../icons/ExpandMore';
import FormHint from './FormHint';
import { PulseLoader } from 'react-spinners';
import { Tooltip } from 'react-tooltip';
import { useMediaQuery } from 'react-responsive';

const control = css`
  border: 1px solid #1a1a1a;
  border-radius: 5px;
  cursor: pointer !important;
  font-size: 18px;
  line-height: 18px;
  min-height: 40px;
  padding: 5.5px 15px;
  width: 100%;

  input:focus {
    box-shadow: none !important;
  }
`;

const controlFocussed = css`
  box-sizing: border-box;
  box-shadow:
    0 0 0 3px #fd0,
    inset 0 0 0 3px #1a1a1a;
`;

const menu = css`
  border: 1px solid #e5dbd1;
  background: #fff;
  box-shadow: 4px 4px 4px 0px #00000033;
  border-radius: 5px;
  left: 0;
  top: calc(100% + 5px) !important;
`;

const menuList = css`
  border-radius: 5px;
`;

const multiValue = css`
  background: #1a1a1a;
  border-radius: 5px;
  color: #fff;
  font-size: 16px;
  padding: 2px 8px;
  line-height: 18px;
  margin: 2px 5px 2px 0;
`;

const multiValueRemove = css`
  margin-left: 5px;
`;

const multiValueFocused = css`
  background-color: #ffdd00;
  color: #1a1a1a;
  outline: 1px solid #1a1a1a !important;
  outline-offset: -1px;

  & * {
    outline: none !important;
  }
`;

const noOptionsMessage = css`
  padding: 5px 15px;
`;

const option = css`
  cursor: pointer !important;
  padding: 5px 15px;

  &:hover {
    background-color: #f2ede8;
  }
`;

const optionFocused = css`
  background-color: #ffdd00;
  outline: 3px solid #1a1a1a !important;
  outline-offset: -3px;
`;

const optionSelected = css`
  box-shadow: inset 0 0 0 3px #1a1a1a;
`;

const optionWrapper = css`
  display: flex;
  align-items: center;
`;

const optionIcon = css`
  fill: currentColor;
  height: 20px;
  margin-right: 15px;
`;

const optionLabel = css`
  line-height: 28px;
  width: 100%;
`;

const selectedValueLabel = css`
  span {
    span:first-child {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      min-width: 0;
    }
  }
`;

const dropdownIcon = css`
  fill: #1a1a1a;
  width: 14px;
  transition: transform 300ms ease-out;
  margin-left: 15px;

  &[data-open='true'] {
    transform: rotate(180deg);
  }
`;

const closeIcon = css`
  fill: #1a1a1a;
  width: 14px;
  margin-left: 10px;
`;

const ClearIndicator = props => (
  <components.ClearIndicator {...props}>
    {<CloseIcon className={closeIcon} />}
  </components.ClearIndicator>
);

const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    {
      <ExpandMoreIcon
        className={dropdownIcon}
        data-open={props.selectProps.menuIsOpen}
      />
    }
  </components.DropdownIndicator>
);

const LoadingIndicator = () => (
  <PulseLoader color="#1a1a1a" margin={1} size={5} />
);

const Option = props => (
  <components.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      onMouseMove: undefined,
      onMouseOver: undefined,
    }}
  >
    <div className={optionWrapper}>
      {props.data.icon && <props.data.icon className={optionIcon} />}
      <span className={optionLabel}>{props.data.label}</span>
    </div>
  </components.Option>
);

const Control = props => {
  const { selectProps } = props;
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const controlRef = useRef(null);

  const isMobile = useMediaQuery({ query: '(max-width: 799px)' });

  useLayoutEffect(() => {
    if (isMobile) {
      setIsTruncated(false);
      setTooltipContent('');
      return;
    }

    const controlNode = controlRef.current;
    if (!controlNode) return;

    const checkTruncation = () => {
      const targetElement =
        controlNode.querySelector('span span span:first-child') ||
        controlNode.querySelector('span span:first-child');

      if (!targetElement) return;

      const truncated = targetElement.scrollWidth > targetElement.clientWidth;
      setIsTruncated(truncated);

      if (truncated) {
        const text = targetElement.innerText || targetElement.textContent;
        setTooltipContent(text.trim());
      }
    };

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(controlNode);

    return () => observer.disconnect();
  }, [selectProps.value, isMobile]);

  return (
    <components.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        ref: node => {
          controlRef.current = node;
          if (typeof props.innerProps.ref === 'function') {
            props.innerProps.ref(node);
          } else if (props.innerProps.ref) {
            props.innerProps.ref.current = node;
          }
        },
        ...(isTruncated &&
          tooltipContent && {
            'data-tooltip-id': `${selectProps.inputId}-tooltip`,
            'data-tooltip-content': tooltipContent,
          }),
      }}
    >
      {props.children}
    </components.Control>
  );
};

const SingleValue = props => {
  return (
    <components.SingleValue {...props}>
      <div className={optionWrapper}>
        {props.data.icon && <props.data.icon className={optionIcon} />}
        <span className={classNames(optionLabel, selectedValueLabel)}>
          {props.data.label}
        </span>
      </div>
    </components.SingleValue>
  );
};

const MultiValue = props => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <components.MultiValue
      {...props}
      className={classNames(props.className, isFocused && multiValueFocused)}
      innerProps={{
        ...props.innerProps,
        tabIndex: 0,
        role: 'button',
        'aria-label': `Remove ${props.data.label}`,
        onFocus: e => {
          setIsFocused(true);
          props.innerProps?.onFocus?.(e);
        },
        onBlur: e => {
          setIsFocused(false);
          props.innerProps?.onBlur?.(e);
        },
        onKeyDown: e => {
          if (
            e.key === 'Delete' ||
            e.key === 'Backspace' ||
            e.key === 'Enter' ||
            e.key === ' '
          ) {
            e.preventDefault();
            props.removeProps.onClick(e);
          }
        },
      }}
    />
  );
};

export default function FormSelect({
  id,
  formHint,
  label,
  components,
  options,
  value,
  async,
  isLastElement,
  ...rest
}) {
  let selectedOption = null;

  if (Array.isArray(value)) {
    selectedOption = value;
  } else {
    selectedOption = value
      ? options
        ? options.find(option => option.value === value)
        : value
      : null;
  }

  const Component = async ? AsyncSelect : Select;

  return (
    <FormField
      aria-describedby={formHint ? `${id}-hint` : undefined}
      isLastElement={isLastElement}
    >
      <FormLabel hasHint={formHint} htmlFor={id}>
        {label}
      </FormLabel>

      {formHint && <FormHint id={`${id}-hint`}>{formHint}</FormHint>}

      <Component
        isClearable
        unstyled
        classNames={{
          control: ({ isFocused }) =>
            classNames(isFocused && controlFocussed, control),
          menu: () => menu,
          menuList: () => menuList,
          multiValue: () => multiValue,
          multiValueRemove: () => multiValueRemove,
          noOptionsMessage: () => noOptionsMessage,
          loadingMessage: () => noOptionsMessage,
          option: ({ isFocused, isSelected }) =>
            classNames(
              option,
              isFocused && optionFocused,
              isSelected && optionSelected,
            ),
        }}
        components={{
          ...components,
          ClearIndicator,
          Control,
          DropdownIndicator,
          LoadingIndicator,
          MultiValue,
          Option,
          SingleValue,
        }}
        inputId={id}
        options={options}
        placeholder="Show all"
        styles={{
          control: baseStyles => ({
            ...baseStyles,
            outline: 'none',
            background: '#ffffff',
          }),
        }}
        value={selectedOption}
        {...rest}
      />
      <Tooltip
        globalCloseEvents={{ escape: true }}
        id={`${id}-tooltip`}
        place="top"
      />
    </FormField>
  );
}
